import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Paper, Typography, TextField, Chip } from "@material-ui/core";
import {
  isMetaCallStageControlConfigured,
  validStageCallId,
  fetchMetaCallStageStatus,
  hangupMetaCallStage,
  isMetaCallStageCreateConfigured,
  createNextMetaCallStage
} from "../../services/metaCallStageControl";

const LAST_TEST_CALL = "wacid.browsermock.1434418.ended04";
const readableStatus = status => ({
  ringing: "Sonando (sin contestar)",
  claimed: "Reclamada / aceptacion simulada",
  terminated: "Finalizada"
}[status] || "No disponible");

const MetaCallStageControls = ({ identity, signal }) => {
  const enabled = Boolean(identity) && isMetaCallStageControlConfigured();
  const [callId, setCallId] = useState(LAST_TEST_CALL);
  const [state, setState] = useState(null);
  const [message, setMessage] = useState("");
  const [manualCheck, setManualCheck] = useState("");
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [busy, setBusy] = useState(false);
  const [createOutcomeUnknown, setCreateOutcomeUnknown] = useState(false);
  const pending = useRef(false);
  const currentIdentity = useRef(identity);
  currentIdentity.current = identity;
  const currentCallId = useRef(callId);
  currentCallId.current = callId;

  useEffect(() => {
    setCallId(LAST_TEST_CALL);
    setState(null);
    setMessage("");
    setCreateOutcomeUnknown(false);
  }, [identity]);

  useEffect(() => { setManualCheck(""); }, [identity, callId]);

  useEffect(() => {
    if (!enabled || !signal || !validStageCallId(signal.callId)) return;
    if (signal.action === "ringing") {
      setCallId(signal.callId);
      setState(null);
      setMessage("Se recibio una llamada de prueba. Consultando el servidor...");
    } else if (signal.action === "ended" && signal.callId === callId) {
      setState(previous => previous && previous.callId === callId
        ? { ...previous, status: "terminated", can_hangup: false } : previous);
      setMessage("El backend notifico la finalizacion por Socket.IO.");
    }
  }, [enabled, signal, callId]);

  useEffect(() => {
    if (!enabled || !validStageCallId(callId)) {
      setState(null);
      return undefined;
    }
    let active = true;
    let inFlight = false;
    const expectedIdentity = identity;
    const refresh = async () => {
      if (!active || inFlight || pending.current) return;
      inFlight = true;
      try {
        const result = await fetchMetaCallStageStatus(callId);
        if (active && currentIdentity.current === expectedIdentity) {
          setState(result);
          setMessage("");
        }
      } catch (error) {
        if (active && currentIdentity.current === expectedIdentity) {
          setState(null);
          setMessage(error.message === "HTTP_404"
            ? "Llamada no disponible para este usuario."
            : "No se pudo consultar el estado. Comprueba la conexion de staging.");
        }
      } finally {
        inFlight = false;
      }
    };
    refresh();
    const timer = window.setInterval(refresh, 3000);
    return () => { active = false; window.clearInterval(timer); };
  }, [identity, enabled, callId]);

  const onCreateNext = async () => {
    if (!enabled || pending.current || busy || createOutcomeUnknown ||
        !isMetaCallStageCreateConfigured() ||
        state?.callId !== callId || state.status !== "terminated") return;
    pending.current = true;
    setBusy(true);
    // Hasta recibir HTTP 201, el resultado es potencialmente incierto.
    // Nunca reintentar automaticamente la creacion.
    setCreateOutcomeUnknown(true);
    setMessage("Generando oferta sin microfono y creando llamada de prueba...");
    const expectedIdentity = identity;
    try {
      const created = await createNextMetaCallStage();
      if (currentIdentity.current !== expectedIdentity) return;
      setCallId(created.callId);
      setState(null);
      setCreateOutcomeUnknown(false);
      setMessage("Nueva llamada sintetica creada. Observa el aviso de Whaticket.");
    } catch (error) {
      if (currentIdentity.current === expectedIdentity) {
        setMessage(error.message === "TEST_CALL_STILL_ACTIVE"
          ? "Ya existe una llamada dinamica activa en staging. No repitas Crear; revisa la sesion."
          : "No se confirmo la creacion. No repitas Crear hasta comprobar el estado en el backend.");
      }
    } finally {
      pending.current = false;
      if (currentIdentity.current === expectedIdentity) setBusy(false);
    }
  };

  const onHangup = async () => {
    if (!enabled || pending.current || !state ||
        state.callId !== callId || state.status !== "claimed" ||
        state.can_hangup !== true) return;
    pending.current = true;
    setBusy(true);
    setMessage("Verificando permisos y estado de la llamada...");
    const expectedIdentity = identity;
    try {
      const latest = await fetchMetaCallStageStatus(callId);
      if (latest.status !== "claimed" || latest.can_hangup !== true) {
        setState(latest);
        setMessage("No se puede finalizar esta llamada en su estado actual.");
        return;
      }
      if (currentIdentity.current !== expectedIdentity) return;
      const result = await hangupMetaCallStage(callId);
      if (currentIdentity.current !== expectedIdentity) return;
      if (result.status !== "ended" || result.provider_contacted !== false) {
        throw new Error("RESULTADO_NO_CONFIRMADO");
      }
      setState({ ...latest, status: "terminated", can_hangup: false });
      setMessage("Finalizacion simulada confirmada. Audio real: no conectado.");
    } catch (_) {
      if (currentIdentity.current === expectedIdentity) {
        setMessage("Resultado no confirmado. Consulta el estado; NO repitas Colgar.");
      }
    } finally {
      pending.current = false;
      if (currentIdentity.current === expectedIdentity) setBusy(false);
    }
  };

  if (!enabled) return null;
  const canHangup = validStageCallId(callId) && state?.callId === callId &&
    state.status === "claimed" && state.can_hangup === true && !busy;

  return (
    <Paper elevation={8} style={{ padding: 14, marginBottom: 8, pointerEvents: "auto", borderLeft: "4px solid #2875a8", borderRadius: 10 }}>
      <Typography variant="subtitle1">Control de llamada - staging</Typography>
      <Typography variant="caption" color="textSecondary">
        Consulta protegida por JWT. No conecta audio ni contacta Meta.
      </Typography>
      <Box mt={1} mb={1}>
        <TextField fullWidth size="small" label="Identificador de prueba"
          value={callId} disabled={busy}
          onChange={event => { setCallId(event.target.value); setState(null); setMessage(""); }} />
      </Box>
      <Box mb={1}>
        <Chip size="small" variant="outlined" label={
          state?.callId === callId ? readableStatus(state.status) : "Sin estado confirmado"
        } />
      </Box>
      {state?.callId === callId && (
        <Typography variant="body2">Conversacion #{state.ticketId || "?"}</Typography>
      )}
      <Box mt={1} mb={1}>
        <Button fullWidth size="small" variant="contained" color="primary"
          disabled={!enabled || busy || createOutcomeUnknown ||
            !isMetaCallStageCreateConfigured() ||
            state?.callId !== callId || state.status !== "terminated"}
          onClick={onCreateNext}>
          Nueva llamada sintetica
        </Button>
      </Box>
      <Box display="flex" gridGap={8} mt={1}>
        <Button size="small" variant="outlined"
          disabled={!validStageCallId(callId) || busy || checkingStatus}
          onClick={async () => {
            if (pending.current || checkingStatus || !validStageCallId(callId)) return;
            const checkedCallId = callId;
            const checkedIdentity = identity;
            setCheckingStatus(true);
            setManualCheck("Consultando el backend...");
            try {
              const result = await fetchMetaCallStageStatus(checkedCallId);
              if (currentIdentity.current === checkedIdentity &&
                  currentCallId.current === checkedCallId) {
                setState(result);
                setManualCheck("Ultima consulta manual: HTTP 200 a las " +
                  new Date().toLocaleTimeString("es-MX") +
                  ". Estado recibido: " + readableStatus(result.status) + ".");
              }
            } catch (error) {
              if (currentIdentity.current === checkedIdentity &&
                  currentCallId.current === checkedCallId) {
                setState(null);
                const code = /^HTTP_[0-9]{3}$/.test(error.message)
                  ? error.message.replace("HTTP_", "HTTP ")
                  : "Error de conexion o sesion";
                setManualCheck("Consulta fallida: " + code + ".");
              }
            } finally {
              setCheckingStatus(false);
            }
          }}>{checkingStatus ? "Consultando..." : "Consultar estado"}</Button>
        <Button size="small" variant="contained" color="secondary"
          disabled={!canHangup} onClick={onHangup}>
          Colgar simulacion
        </Button>
      </Box>
      {manualCheck && <Typography variant="caption" style={{ display: "block", marginTop: 8 }} aria-live="polite">
        {manualCheck}
      </Typography>}
      {message && <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 8 }}>
        {message}
      </Typography>}
    </Paper>
  );
};

export default MetaCallStageControls;
